import { IconChevronLeft, IconChevronRight } from './icons.js';
import { pageList } from './page-list.js';
import styles from './Pagination.module.css';

/**
 * Rows per page, for every paginated table.
 *
 * One list, so two tables cannot offer different choices — which is exactly
 * what happened: the contacts table started at 25 and the interactions table at
 * 10, and the same control behaved differently on two pages of one console.
 */
export const PAGE_SIZES = [25, 50, 100] as const;

interface Props {
  page: number;
  pageSize: number;
  /** Rows matching the current filter, not rows on this page. */
  total: number;
  onPage: (page: number) => void;
  onPageSize: (pageSize: number) => void;
}

/**
 * The pagination bar (S05, S08).
 *
 * Shared rather than reimplemented per table. The two copies this replaces had
 * already drifted in five ways — page sizes, corner radius, button height, gap
 * and the active-page treatment — which is the kind of difference nobody
 * notices in one place and everybody notices moving between two.
 */
export function Pagination({ page, pageSize, total, onPage, onPageSize }: Props): JSX.Element {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className={styles.pagination}>
      <label className={styles.pgSize}>
        Rows per page
        <select
          className={styles.pgSelect}
          value={pageSize}
          onChange={(event) => onPageSize(Number(event.target.value))}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>

      <span className={styles.pgInfo}>
        {first}&ndash;{last} of {total}
      </span>

      <div className={styles.pgControls}>
        <button
          type="button"
          className={styles.pgBtn}
          disabled={page === 1}
          aria-label="Previous page"
          onClick={() => onPage(Math.max(1, page - 1))}
        >
          <IconChevronLeft />
        </button>

        {pageList(page, pageCount).map((entry, index) =>
          entry === '…' ? (
            <span className={styles.pgGap} key={`gap-${String(index)}`}>
              …
            </span>
          ) : (
            <button
              type="button"
              key={entry}
              className={`${styles.pgBtn} ${entry === page ? styles.pgActive : ''}`}
              aria-current={entry === page ? 'page' : undefined}
              onClick={() => onPage(entry)}
            >
              {entry}
            </button>
          ),
        )}

        <button
          type="button"
          className={styles.pgBtn}
          disabled={page >= pageCount}
          aria-label="Next page"
          onClick={() => onPage(Math.min(pageCount, page + 1))}
        >
          <IconChevronRight />
        </button>
      </div>
    </div>
  );
}
